import React, { ReactNode, useMemo } from "react";
import {
  Paper,
  Typography,
  IconButton,
  SxProps,
  Theme,
  Stack,
  Box,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { IconClose } from "@/icons";

export interface BaseFilterPillProps {
  icon?: ReactNode;
  label: string;
  onRemove: () => void;
  children: ReactNode;
  background?: string;
  sx?: SxProps<Theme>;
}

const BaseFilterPill: React.FC<BaseFilterPillProps> = ({
  icon,
  label,
  onRemove,
  children,
  background = "#f0f7ff", // Default blue background
  sx = {},
}) => {
  const [hoverBackground, buttonBackground] = useMemo(() => {
    // If a custom hover background is provided, use it
    // Otherwise, make the background color darker by 0.05 (5%)
    return [darken(background, 0.03), darken(background, 0.08)];
  }, [background]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: "12px",
        backgroundColor: background,
        pl: 2,
        height: 40,
        overflow: "hidden",
        ...sx,
        "&:hover": {
          backgroundColor: hoverBackground,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {icon && (
          <Box sx={{ marginRight: 1, height: "22px", opacity: 0.7 }}>
            {icon}
          </Box>
        )}

        {label && (
          <Typography variant="body2" fontWeight="medium" mr={1}>
            {label}:
          </Typography>
        )}

        {children}

        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            borderRadius: "100%",
            marginRight: 1,
            height: "100%",
            "&:hover": {
              backgroundColor: buttonBackground, // Make button hover even darker
            },
          }}
        >
          <IconClose fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default BaseFilterPill;
